/**
 * RecordScrapModal — structured trim/material scrap recording (BLU-534 gap-fill).
 *
 * Opened from a single already-resolved (SKU, location) pair — no catalog
 * picker needed, unlike UnavailableAdjustmentModal (this mirrors its Dialog
 * shape/interaction conventions, not its cross-org picker). Deducts stock
 * immediately via `inventory.recordScrap` and writes a `sku_scrap_events`
 * audit row (reason / optional PO reference / notes).
 *
 * v1 scope: the PO reference is a free-text field folded into `notes` — the
 * backend mutation also accepts a `referencePoId` (uuid FK), but no trivial
 * PO-number → id picker/lookup existed yet, so this v1 UI doesn't wire it.
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { justInTimeStockRefusal } from "../utils/justInTimeCopy";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

const SCRAP_REASON_OPTIONS = [
  { value: "supplier_scrap", label: "Supplier scrap" },
  { value: "damaged_in_transit", label: "Damaged in transit" },
  // lost_in_transit is deliberately absent: it is reserved for the audit-only
  // rows a transfer writes when shipped units never arrive. A manual scrap
  // decrements stock, so offering it here would mix the two meanings.
  { value: "qc_reject", label: "QC reject" },
  { value: "other", label: "Other" },
] as const;
type ScrapReason = (typeof SCRAP_REASON_OPTIONS)[number]["value"];

interface RecordScrapModalProps {
  isOpen: boolean;
  onClose: () => void;
  sku: string;
  skuId: string;
  location: { locationId: string; locationName: string; onHand: number };
  onRecorded?: () => void;
}

const RecordScrapModal: React.FC<RecordScrapModalProps> = ({
  isOpen,
  onClose,
  sku,
  skuId,
  location,
  onRecorded,
}) => {
  const { t } = useTranslation("inventory");
  const [quantity, setQuantity] = useState<number>(1);
  const [reason, setReason] = useState<ScrapReason>("supplier_scrap");
  const [poReference, setPoReference] = useState("");
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!isOpen) return;
    setQuantity(1);
    setReason("supplier_scrap");
    setPoReference("");
    setNotes("");
  }, [isOpen, location.locationId]);

  const recordScrapMut = trpc.inventory.recordScrap.useMutation({
    onSuccess: () => {
      toast.success(`Recorded ${quantity} scrapped: ${sku} @ ${location.locationName}`);
      onRecorded?.();
      onClose();
    },
    onError: (error) => {
      // BLU-1268: a just-in-time pair has no stock to scrap from; say so in words.
      toast.error(justInTimeStockRefusal(t, error) ?? `Failed to record scrap: ${error.message}`);
    },
  });

  const disableSubmit = quantity <= 0 || !Number.isFinite(quantity) || recordScrapMut.isPending;

  const handleSubmit = () => {
    if (disableSubmit) return;
    // v1: PO reference is free text — folded into notes rather than the
    // (currently unwired) referencePoId. See file header.
    const combinedNotes = [
      poReference.trim() ? `PO: ${poReference.trim()}` : null,
      notes.trim() || null,
    ]
      .filter(Boolean)
      .join(". ");

    recordScrapMut.mutate({
      skuId,
      locationId: location.locationId,
      quantity,
      reason,
      notes: combinedNotes || undefined,
      // Structured operator reference (Thread Lab stock log): lands on
      // inventory_transactions.reference_id (reference_type stays 'scrap')
      // so the ledger's Order/Ref column is filterable. The notes fold-in
      // above is kept for the human-readable trail.
      reference: poReference.trim().slice(0, 64) || undefined,
    });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if ((event.metaKey || event.ctrlKey) && event.key === "Enter") {
      if (!disableSubmit) {
        event.preventDefault();
        handleSubmit();
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[420px]" onKeyDown={handleKeyDown}>
        <DialogHeader>
          <DialogTitle>Record scrap</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            Trims/materials lost to damage, QC rejects, or supplier issues. Deducts stock
            immediately.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div className="rounded-lg bg-muted/30 p-3 text-sm">
            <p className="font-mono font-medium">{sku}</p>
            <p className="text-xs text-muted-foreground">
              {location.locationName} · {location.onHand} on hand
            </p>
          </div>

          <div className="grid gap-2">
            <Label>Quantity</Label>
            <Input
              type="number"
              min={1}
              value={Number.isNaN(quantity) ? "" : quantity}
              onChange={(e) => setQuantity(parseInt(e.target.value, 10) || 0)}
            />
          </div>

          <div className="grid gap-2">
            <Label>Reason</Label>
            <Select value={reason} onValueChange={(v) => setReason(v as ScrapReason)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {SCRAP_REASON_OPTIONS.map((o) => (
                  <SelectItem key={o.value} value={o.value}>
                    {o.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label>PO reference (optional)</Label>
            <Input
              placeholder="PO number"
              value={poReference}
              onChange={(e) => setPoReference(e.target.value)}
            />
          </div>

          <div className="grid gap-2">
            <Label>Notes (optional)</Label>
            <Textarea
              placeholder="Any additional detail…"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={disableSubmit}
            className="bg-threadheads-purple text-white hover:bg-threadheads-purple/90 disabled:opacity-50"
            aria-keyshortcuts="Meta+Enter"
            aria-busy={recordScrapMut.isPending || undefined}
          >
            {recordScrapMut.isPending && (
              <Loader2 className="mr-1 h-4 w-4 animate-spin" aria-hidden />
            )}
            Record scrap
            {!recordScrapMut.isPending && (
              <kbd className="ml-1 rounded border border-border/60 bg-white/10 px-1 text-[10px] font-mono">
                ⌘↵
              </kbd>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default RecordScrapModal;
